import { useState, useEffect } from "react";
import { supabase, BRAND, BRAND_LIGHT, SHADOW_MODAL, SHADOW_SM, SCRIM, LINE, NEUTRAL_BG, FAINT, WHITE, MUTED, TEXT, INK, SURFACE, ROW_LINE, ARCH_QUAD_FILLS, ARCH_QUAD_TEXT, ARCH_COLORS, CULTURE_COLORS } from "./constants";
import { CULTURE_DIMS } from "./lib/culture";
import { heat } from "./PositionEvaluationTab";
import { useIsMobile } from "./lib/responsive";
import { LOGO_FONT } from "./Logo";

// ─── Fallback avatar colors when photos can't be loaded ─────────────────────
// Los pastel de datos de la paleta (31/10), no colores propios
const AVATAR_PALETTE = ARCH_QUAD_FILLS.map((bg, i) => ({ bg, fg: ARCH_QUAD_TEXT[i] }));

const initialsOf = (name) => {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

// ─── Reusable circular avatar (photo with fallback to colored initials) ────
const SvgAvatar = ({ cx, cy, r, photo, name, paletteIndex = 0, clipId }) => {
  const palette = AVATAR_PALETTE[paletteIndex % AVATAR_PALETTE.length];
  if (photo) {
    return (
      <g>
        <defs>
          <clipPath id={clipId}>
            <circle cx={cx} cy={cy} r={r} />
          </clipPath>
        </defs>
        <image href={photo} x={cx - r} y={cy - r} width={r * 2} height={r * 2}
          clipPath={`url(#${clipId})`} preserveAspectRatio="xMidYMid slice" />
        <circle cx={cx} cy={cy} r={r} fill="none" stroke={LINE} strokeWidth="0.8" />
      </g>
    );
  }
  return (
    <g>
      <circle cx={cx} cy={cy} r={r} fill={palette.bg} />
      <text x={cx} y={cy + Math.round(r * 0.32)} textAnchor="middle"
        style={{ fontSize: Math.max(7, r * 0.78), fontWeight: 800, fill: palette.fg }}>
        {initialsOf(name)}
      </text>
    </g>
  );
};

// Perfil anónimo del mapping: silueta gris, nunca foto ni iniciales.
const AnonAvatar = ({ cx, cy, r = 10 }) => (
  <g>
    <circle cx={cx} cy={cy} r={r} fill={NEUTRAL_BG} />
    <circle cx={cx} cy={cy - r * 0.25} r={r * 0.34} fill={FAINT} />
    <path d={`M${cx - r * 0.6},${cy + r * 0.72} a${r * 0.6},${r * 0.5} 0 0 1 ${r * 1.2},0`} fill={FAINT} />
  </g>
);

// ─── Piezas comunes de los mock-ups ─────────────────────────────────────────
// Todas las ilustraciones comparten marco (fondo BRAND_LIGHT + tarjeta blanca
// radio 14, borde #EFEFEF) y los mismos conmutadores grises del sistema: el
// activo en blanco con texto BRAND. Las coordenadas internas van de 0 a 640
// en horizontal y de 0 a 252 en vertical.

const Frame = ({ children }) => (
  <svg viewBox="0 0 720 280" xmlns="http://www.w3.org/2000/svg" style={{ width: "100%", height: "100%" }}>
    <rect width="720" height="280" fill={BRAND_LIGHT} rx="14" />
    <g transform="translate(40, 14)">
      <rect x="0" y="0" width="640" height="252" rx="14" fill={WHITE} stroke={LINE} strokeWidth="1.5" />
      {children}
    </g>
  </svg>
);

// Parte un texto en líneas de como mucho `max` caracteres (SVG no hace saltos).
const wrap = (text, max) => {
  const out = [];
  let line = "";
  text.split(" ").forEach((w) => {
    const next = line ? `${line} ${w}` : w;
    if (next.length > max && line) { out.push(line); line = w; } else line = next;
  });
  if (line) out.push(line);
  return out;
};

const Lines = ({ x, y, lines, lh = 10, style }) => (
  <text x={x} y={y} style={style}>
    {lines.map((l, i) => <tspan key={i} x={x} dy={i === 0 ? 0 : lh}>{l}</tspan>)}
  </text>
);

const Label = ({ x, y, children, anchor = "start" }) => (
  <text x={x} y={y} textAnchor={anchor} style={{ fontSize: 7, fontWeight: 700, fill: MUTED, letterSpacing: 0.4 }}>{children}</text>
);

const segWidths = (items, fs) => items.map(([, l]) => Math.round(l.length * fs * 0.55 + 20));

// Conmutador gris del sistema. `x` = borde izquierdo, o `xRight` = borde derecho.
const Segmented = ({ items, active, x, xRight, y, fs = 9, h = 24 }) => {
  const ws = segWidths(items, fs);
  const total = ws.reduce((a, b) => a + b, 0) + 6;
  const left = xRight !== undefined ? xRight - total : x;
  let cx = left + 3;
  return (
    <g>
      <rect x={left} y={y} width={total} height={h} rx="8" fill={NEUTRAL_BG} />
      {items.map(([k, l], i) => {
        const ix = cx;
        cx += ws[i];
        const on = k === active;
        return (
          <g key={k}>
            {on && <rect x={ix} y={y + 3} width={ws[i]} height={h - 6} rx="6" fill={WHITE} stroke={LINE} strokeWidth="0.6" />}
            <text x={ix + ws[i] / 2} y={y + h / 2 + fs * 0.36} textAnchor="middle"
              style={{ fontSize: fs, fontWeight: 600, fill: on ? BRAND : TEXT }}>{l}</text>
          </g>
        );
      })}
    </g>
  );
};

// Cabecera de una posición con sus cuatro pestañas.
const PosHeader = ({ lang, active }) => {
  const es = lang === "es";
  const tabs = [
    ["mapping", "Mapping"],
    ["candidates", es ? "Candidatos" : "Candidates"],
    ["evaluation", es ? "Evaluación" : "Evaluation"],
    ["documents", "Brief"],
  ];
  return (
    <g>
      <text x="20" y="25" style={{ fontSize: 13, fontWeight: 800, fill: INK }}>Chief Operating Officer</text>
      <text x="20" y="38" style={{ fontSize: 8, fill: MUTED }}>Matteco · Madrid</text>
      <Segmented items={tabs} active={active} xRight={620} y={11} />
      <line x1="20" y1="47" x2="620" y2="47" stroke={LINE} strokeWidth="1" />
    </g>
  );
};

// Cabecera de la ficha de candidato con sus tres pestañas.
const ProfileHeader = ({ lang, active, photo }) => {
  const es = lang === "es";
  const tabs = [
    ["report", es ? "Informe" : "Report"],
    ["evaluation", es ? "Evaluación" : "Evaluation"],
    ["compare", es ? "Comparar" : "Compare"],
  ];
  return (
    <g>
      <SvgAvatar cx={34} cy={27} r={14} photo={photo} name="María Yáñez" paletteIndex={0} clipId={`onb-prof-${active}`} />
      <text x="56" y="24" style={{ fontSize: 13, fontWeight: 800, fill: INK }}>María Yáñez</text>
      <text x="56" y="37" style={{ fontSize: 8, fill: MUTED }}>Director Strategy &amp; Ops · UBS</text>
      <Segmented items={tabs} active={active} xRight={620} y={13} />
      <line x1="20" y1="49" x2="620" y2="49" stroke={LINE} strokeWidth="1" />
    </g>
  );
};

const Chevron = ({ x, y }) => (
  <path d={`M${x},${y} l3,3 l3,-3`} fill="none" stroke={MUTED} strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round" />
);

const Chip = ({ x, y, w, children, bg = NEUTRAL_BG, fg = TEXT }) => (
  <g>
    <rect x={x} y={y} width={w} height="14" rx="7" fill={bg} />
    <text x={x + w / 2} y={y + 10} textAnchor="middle" style={{ fontSize: 7, fontWeight: 700, fill: fg }}>{children}</text>
  </g>
);

const PctChip = ({ cx, cy, pct, w = 34 }) => {
  const c = heat(pct);
  return (
    <g>
      <rect x={cx - w / 2} y={cy - 7} width={w} height="14" rx="7" fill={c.background} />
      <text x={cx} y={cy + 3} textAnchor="middle" style={{ fontSize: 7.5, fontWeight: 700, fill: c.color }}>{Math.round(pct)}%</text>
    </g>
  );
};

const Bullet = ({ x, y, lines, lh = 10 }) => (
  <g>
    <circle cx={x + 2} cy={y - 3} r="1.8" fill={FAINT} />
    <Lines x={x + 9} y={y} lines={lines} lh={lh} style={{ fontSize: 8, fill: INK }} />
  </g>
);

const VisualWelcome = () => (
  <svg viewBox="0 0 720 280" xmlns="http://www.w3.org/2000/svg" style={{ width: "100%", height: "100%" }}>
    <rect width="720" height="280" fill={BRAND_LIGHT} rx="14" />
    {/* Logo (mismas proporciones que src/Logo.jsx a 64 px): palabra alineada a la derecha y bloque cursor */}
    <text x="463" y="160" textAnchor="end"
      style={{ fontFamily: LOGO_FONT, fontWeight: 700, fontSize: 64, fill: BRAND, letterSpacing: "-0.05em" }}>
      rutment
    </text>
    <rect x="475" y="105" width="29" height="64" rx="5" fill={BRAND} />
    <circle cx="120" cy="80" r="4" fill={BRAND} opacity="0.3" />
    <circle cx="600" cy="200" r="4" fill={BRAND} opacity="0.3" />
    <rect x="80" y="180" width="36" height="36" rx="4" fill="none" stroke={BRAND} strokeWidth="1.5" opacity="0.3" />
    <rect x="604" y="60" width="36" height="36" rx="4" fill="none" stroke={BRAND} strokeWidth="1.5" opacity="0.3" />
    <circle cx="540" cy="60" r="3" fill={BRAND} opacity="0.2" />
    <circle cx="180" cy="220" r="3" fill={BRAND} opacity="0.2" />
  </svg>
);

// ─── 1. Brief: intake del puesto + cuestionario de cultura → los 4 vectores ──
// Mismos nombres que translations (constants.js): technicalFit, softSkills…
const VECTORS = (es) => es
  ? ["Fit técnico", "Soft skills", "Fit cultural", "Potencial de crecimiento"]
  : ["Technical fit", "Soft skills", "Cultural fit", "Growth potential"];

const VisualBrief = ({ lang }) => {
  const es = lang === "es";
  const musts = es ? ["Ops de 50 a 300", "P&L propio", "Español e inglés"] : ["Scaled ops 50→300", "Owns a P&L", "Spanish & English"];
  let mx = 34;
  const mustChips = musts.map((m) => {
    const w = Math.round(m.length * 4.3 + 16);
    const out = { m, x: mx, w };
    mx += w + 6;
    return out;
  });
  const qa = es
    ? [["¿Cómo se toman las decisiones?", "Rápido: decide quien tiene el problema."], ["¿Cómo es una mala semana?", "Muchas reuniones y nadie decide."]]
    : [["How are decisions made?", "Fast: whoever owns the problem decides."], ["What does a bad week look like?", "Too many meetings, nobody deciding."]];
  return (
    <Frame>
      {/* El puesto (intake) */}
      <rect x="20" y="18" width="290" height="150" rx="12" fill={WHITE} stroke={LINE} strokeWidth="1.5" />
      <Label x={34} y={36}>{es ? "EL PUESTO · INTAKE" : "THE ROLE · INTAKE"}</Label>
      <text x="34" y="52" style={{ fontSize: 11, fontWeight: 800, fill: INK }}>Chief Operating Officer</text>
      <Label x={34} y={70}>{es ? "IMPRESCINDIBLE" : "MUST-HAVES"}</Label>
      {mustChips.map((c) => (
        <g key={c.m}>
          <rect x={c.x} y={76} width={c.w} height="16" rx="8" fill={NEUTRAL_BG} />
          <text x={c.x + c.w / 2} y={87} textAnchor="middle" style={{ fontSize: 7.5, fontWeight: 600, fill: TEXT }}>{c.m}</text>
        </g>
      ))}
      <Label x={34} y={110}>OFF LIMITS</Label>
      <text x="34" y="122" style={{ fontSize: 8, fill: TEXT }}>{es ? "Clientes actuales · Nadie de Nordia" : "Current clients · Nobody from Nordia"}</text>
      <Label x={34} y={140}>{es ? "BANDA SALARIAL" : "SALARY BAND"}</Label>
      <text x="34" y="152" style={{ fontSize: 8, fill: TEXT }}>{es ? "140–170 k€ + 20 % variable" : "€140–170k + 20% bonus"}</text>

      {/* La cultura (cuestionario → culture doc) */}
      <rect x="330" y="18" width="290" height="150" rx="12" fill={WHITE} stroke={LINE} strokeWidth="1.5" />
      <Label x={344} y={36}>{es ? "LA CULTURA · CUESTIONARIO" : "THE CULTURE · QUESTIONNAIRE"}</Label>
      {qa.map(([q, a], i) => (
        <g key={q}>
          <text x="344" y={54 + i * 32} style={{ fontSize: 8.5, fontWeight: 700, fill: INK }}>{q}</text>
          <text x="344" y={66 + i * 32} style={{ fontSize: 8, fill: TEXT }}>{a}</text>
        </g>
      ))}
      <rect x="344" y="116" width="112" height="22" rx="8" fill={BRAND_LIGHT} />
      <path d="M354,121 h7 l3,3 v9 h-10 z" fill="none" stroke={BRAND} strokeWidth="1.1" strokeLinejoin="round" />
      <text x="370" y="130.5" style={{ fontSize: 8, fontWeight: 700, fill: BRAND }}>{es ? "Culture doc listo" : "Culture doc ready"}</text>

      {/* Conectores hacia la vara de medir */}
      <path d="M165,168 v10 H475 v-10 M320,178 v10" fill="none" stroke={LINE} strokeWidth="1.2" />
      <Label x={320} y={200} anchor="middle">{es ? "LA VARA CON LA QUE SE MIDE A CADA CANDIDATO" : "THE YARDSTICK FOR EVERY CANDIDATE"}</Label>
      {VECTORS(es).map((v, i) => {
        const x = 21 + i * 153;
        return (
          <g key={v}>
            <rect x={x} y={208} width="140" height="28" rx="8" fill={SURFACE} stroke={LINE} strokeWidth="1" />
            <circle cx={x + 14} cy={222} r="3" fill={BRAND} />
            <text x={x + 24} y={225} style={{ fontSize: 9, fontWeight: 700, fill: INK }}>{v}</text>
          </g>
        );
      })}
    </Frame>
  );
};

// ─── 2. Mapping: empresas objetivo + perfiles anónimos, sin nombres ─────────
const MAP_STATUS = {
  proposed: { en: "Proposed", es: "Propuesta", bg: NEUTRAL_BG, fg: MUTED },
  validated: { en: "Validated", es: "Validada", bg: BRAND_LIGHT, fg: BRAND },
  vetoed: { en: "Vetoed", es: "Vetada", bg: NEUTRAL_BG, fg: TEXT },
};
const PROFILE_STATUS = {
  proposed: { en: "Proposed", es: "Propuesto" },
  validated: { en: "Validated", es: "Validado" },
  vetoed: { en: "Vetoed", es: "Vetado" },
};

const MappingFooter = ({ es }) => (
  <g>
    <text x="20" y="231" style={{ fontSize: 8, fill: MUTED }}>
      {es ? "No contactamos a nadie hasta que apruebas el mapping." : "Nobody is contacted until you approve the mapping."}
    </text>
    <rect x="500" y="215" width="120" height="24" rx="8" fill={BRAND} />
    <text x="560" y="230" textAnchor="middle" style={{ fontSize: 9, fontWeight: 700, fill: WHITE }}>{es ? "Aprobar mapping" : "Approve mapping"}</text>
  </g>
);

const VisualMappingCompanies = ({ lang }) => {
  const es = lang === "es";
  const rows = [
    { n: "Cobalto Pay", s: "Fintech", why: es ? "Escaló ops de 50 a 400" : "Scaled ops from 50 to 400", st: "validated", c: es ? "Sí, buen banquillo de ops" : "Yes, strong ops bench" },
    { n: "Lumen Health", s: "Healthtech", why: es ? "Despliegue en varios países" : "Multi-country rollout", st: "proposed", c: "" },
    { n: "Nordia Logistics", s: es ? "Logística" : "Logistics", why: es ? "Mucha operación, misma fase" : "Ops-heavy, same stage", st: "vetoed", c: es ? "Veto: es cliente nuestro" : "Veto: they're a client" },
    { n: "Teselia", s: "SaaS B2B", why: es ? "Serie B, modelo PLG" : "Series B, PLG motion", st: "validated", c: es ? "Mirad también EMEA" : "Look at EMEA too" },
    { n: "Brava Retail", s: "E-commerce", why: es ? "Equipo corto, mucho volumen" : "Lean team, high volume", st: "proposed", c: "" },
  ];
  return (
    <Frame>
      <PosHeader lang={lang} active="mapping" />
      <Label x={20} y={64}>{es ? "EMPRESAS OBJETIVO" : "TARGET COMPANIES"}</Label>
      <text x="620" y="64" textAnchor="end" style={{ fontSize: 8, fill: MUTED }}>{es ? "2 validadas · 1 vetada · 2 propuestas" : "2 validated · 1 vetoed · 2 proposed"}</text>
      <rect x="20" y="71" width="600" height="17" rx="6" fill={SURFACE} />
      <g>
        <Label x={30} y={82}>{es ? "EMPRESA" : "COMPANY"}</Label>
        <Label x={150} y={82}>SECTOR</Label>
        <Label x={245} y={82}>{es ? "POR QUÉ" : "WHY"}</Label>
        <Label x={420} y={82}>{es ? "ESTADO" : "STATUS"}</Label>
        <Label x={495} y={82}>{es ? "COMENTARIOS" : "COMMENTS"}</Label>
      </g>
      {rows.map((r, i) => {
        const y = 88 + i * 24;
        const st = MAP_STATUS[r.st];
        const vetoed = r.st === "vetoed";
        return (
          <g key={r.n}>
            {i > 0 && <line x1="20" y1={y} x2="620" y2={y} stroke={ROW_LINE} strokeWidth="1" />}
            <text x="30" y={y + 15} style={{ fontSize: 8.5, fontWeight: 700, fill: vetoed ? FAINT : INK }}>{r.n}</text>
            <text x="150" y={y + 15} style={{ fontSize: 8, fill: vetoed ? FAINT : TEXT }}>{r.s}</text>
            <text x="245" y={y + 15} style={{ fontSize: 8, fill: vetoed ? FAINT : TEXT }}>{r.why}</text>
            <Chip x={420} y={y + 5} w={58} bg={st.bg} fg={st.fg}>{es ? st.es : st.en}</Chip>
            <text x="495" y={y + 15} style={{ fontSize: 8, fill: r.c ? TEXT : FAINT }}>{r.c || "—"}</text>
          </g>
        );
      })}
      <MappingFooter es={es} />
    </Frame>
  );
};

const VisualMappingProfiles = ({ lang }) => {
  const es = lang === "es";
  // Sin nombres, sin fotos y sin la empresa real: solo rol y descriptor genérico.
  const rows = [
    { role: "Head of Operations", d: es ? "Scale-up SaaS, Serie B" : "SaaS scale-up, Series B", yrs: 12, loc: "Madrid", hl: es ? "Ops de 5 a 40 · 3 mercados" : "Ops team 5→40 · 3 markets", st: "validated", c: es ? "El primero de la lista" : "Top of the list" },
    { role: "COO", d: es ? "Fintech, 200 personas" : "Fintech, 200 people", yrs: 15, loc: "Barcelona", hl: es ? "P&L propio · Integración" : "P&L owner · Post-merger", st: "proposed", c: es ? "¿Qué preaviso tiene?" : "Notice period?" },
    { role: "VP Operations", d: es ? "Marketplace, Serie C" : "Marketplace, Series C", yrs: 10, loc: es ? "Lisboa" : "Lisbon", hl: es ? "Unit economics · En remoto" : "Unit economics · Remote", st: "vetoed", c: es ? "Veto: muy junior" : "Veto: too junior" },
    { role: "Director of Operations", d: es ? "Grupo logístico cotizado" : "Listed logistics group", yrs: 14, loc: "Madrid", hl: es ? "Lean · Equipo de 300" : "Lean · 300-person org", st: "validated", c: "" },
  ];
  return (
    <Frame>
      <PosHeader lang={lang} active="mapping" />
      <Label x={20} y={64}>{es ? "PERFILES ANÓNIMOS" : "ANONYMISED PROFILES"}</Label>
      <text x="620" y="64" textAnchor="end" style={{ fontSize: 8, fill: MUTED }}>
        {es ? "El nombre solo aparece cuando la persona acepta compartirlo" : "Names appear only once the person agrees to share them"}
      </text>
      <rect x="20" y="71" width="600" height="17" rx="6" fill={SURFACE} />
      <g>
        <Label x={30} y={82}>{es ? "PERFIL" : "PROFILE"}</Label>
        <Label x={212} y={82}>{es ? "AÑOS" : "YEARS"}</Label>
        <Label x={252} y={82}>{es ? "UBICACIÓN" : "LOCATION"}</Label>
        <Label x={312} y={82}>{es ? "DESTACADO" : "HIGHLIGHTS"}</Label>
        <Label x={436} y={82}>{es ? "ESTADO" : "STATUS"}</Label>
        <Label x={508} y={82}>{es ? "COMENTARIOS" : "COMMENTS"}</Label>
      </g>
      {rows.map((r, i) => {
        const y = 88 + i * 30;
        const st = { ...MAP_STATUS[r.st], ...PROFILE_STATUS[r.st] };
        const vetoed = r.st === "vetoed";
        const ink = vetoed ? FAINT : TEXT;
        return (
          <g key={r.role}>
            {i > 0 && <line x1="20" y1={y} x2="620" y2={y} stroke={ROW_LINE} strokeWidth="1" />}
            <AnonAvatar cx={40} cy={y + 15} />
            <text x="56" y={y + 13} style={{ fontSize: 8.5, fontWeight: 700, fill: vetoed ? FAINT : INK }}>{r.role}</text>
            <text x="56" y={y + 24} style={{ fontSize: 7.5, fill: vetoed ? FAINT : MUTED }}>{r.d}</text>
            <text x="212" y={y + 18} style={{ fontSize: 8, fill: ink }}>{r.yrs} {es ? "años" : "yrs"}</text>
            <text x="252" y={y + 18} style={{ fontSize: 8, fill: ink }}>{r.loc}</text>
            <text x="312" y={y + 18} style={{ fontSize: 8, fill: ink }}>{r.hl}</text>
            <Chip x={436} y={y + 8} w={58} bg={st.bg} fg={st.fg}>{es ? st.es : st.en}</Chip>
            <text x="508" y={y + 18} style={{ fontSize: 8, fill: r.c ? TEXT : FAINT }}>{r.c || "—"}</text>
          </g>
        );
      })}
      <MappingFooter es={es} />
    </Frame>
  );
};

// ─── 3. Cualificación: el Tablero y el Informe de la ficha ──────────────────
const BOARD = [
  { en: "Pending", es: "Pendientes", cards: [
    { name: "Laura Ibáñez", sub: "Chief of Staff · Cobee", q: "3.5" },
    { name: "Pablo Ortega", sub: "CFO · Glovo", q: "3.4" },
  ] },
  { en: "Validated", es: "Validados", cards: [
    { name: "Andrés Molina", sub: "COO · Flywire", q: "4.3" },
    { name: "Beatriz López", sub: "Dir. Operations · Sherpa", q: "4.5" },
  ] },
  { en: "Interview", es: "Entrevista", cards: [
    { name: "Sofía Pérez", sub: "Dir. Consulting · Bain", q: "3.8" },
  ] },
  { en: "References", es: "Referencias", cards: [
    { name: "María Yáñez", sub: "Strategy & Ops · UBS", q: "4.3" },
  ] },
];

const VisualBoard = ({ photos = {}, lang }) => {
  const es = lang === "es";
  let pi = 0;
  return (
    <Frame>
      <PosHeader lang={lang} active="candidates" />
      {/* Sin selector de vistas: Tabla y Wall están congeladas (20/10) */}
      {BOARD.map((col, ci) => {
        const x = 20 + ci * 152;
        return (
          <g key={col.en}>
            <rect x={x} y={82} width="142" height="160" rx="12" fill={SURFACE} stroke={LINE} strokeWidth="1.5" />
            <text x={x + 10} y={99} style={{ fontSize: 9, fontWeight: 800, fill: INK }}>
              {es ? col.es : col.en}
            </text>
            <circle cx={x + 128} cy={96} r="7" fill={WHITE} stroke={LINE} strokeWidth="1" />
            <text x={x + 128} y={98.5} textAnchor="middle" style={{ fontSize: 7, fontWeight: 700, fill: MUTED }}>{col.cards.length}</text>
            {col.cards.map((c, i) => {
              const y = 108 + i * 52;
              const idx = pi++;
              return (
                <g key={c.name}>
                  <rect x={x + 7} y={y} width="128" height="46" rx="10" fill={WHITE} stroke={LINE} strokeWidth="1.5" />
                  <SvgAvatar cx={x + 22} cy={y + 15} r={9} photo={photos[c.name] || null} name={c.name} paletteIndex={idx} clipId={`onb-board-${idx}`} />
                  <text x={x + 36} y={y + 14} style={{ fontSize: 8.5, fontWeight: 700, fill: INK }}>{c.name}</text>
                  <text x={x + 36} y={y + 24} style={{ fontSize: 6.5, fill: MUTED }}>{c.sub}</text>
                  <rect x={x + 14} y={y + 29} width="52" height="12" rx="6" fill={BRAND_LIGHT} />
                  <text x={x + 40} y={y + 37.5} textAnchor="middle" style={{ fontSize: 6.5, fontWeight: 700, fill: BRAND }}>
                    {es ? "Cual." : "Qual."} {c.q}<tspan style={{ fill: MUTED }}>/5</tspan>
                  </text>
                </g>
              );
            })}
          </g>
        );
      })}
    </Frame>
  );
};

const VisualReport = ({ photos = {}, lang }) => {
  const es = lang === "es";
  const scores = [5, 4, 4, 4];
  const why = es ? [
    "Ha dirigido estrategia y operaciones de una unidad de 300 personas. Ha hecho este mismo rol dos veces.",
    "Comunica con claridad y estructura. Menos probada liderando en conflicto abierto.",
    "Cómoda con la velocidad y la ambigüedad, aunque viene de entornos con más proceso que el vuestro.",
    "Ha ampliado su alcance en cada cambio. El siguiente paso natural es su primer puesto de COO.",
  ] : [
    "Ran strategy and operations for a 300-person unit. Has done this exact role twice before.",
    "Clear, structured communicator. Less tested leading through open conflict.",
    "Comfortable with speed and ambiguity, though she comes from more process than you have.",
    "Took on broader scope with every move. A first COO seat is her natural next step.",
  ];
  const sections = [["cv", "CV"], ["qual", es ? "Cualificación" : "Qualification"], ["refs", es ? "Referencias" : "References"]];
  return (
    <Frame>
      <ProfileHeader lang={lang} active="report" photo={photos["María Yáñez"] || null} />

      {/* Resumen fijo a la izquierda */}
      <rect x="20" y="58" width="208" height="184" rx="12" fill={WHITE} stroke={LINE} strokeWidth="1.5" />
      <text x="32" y="76" style={{ fontSize: 10, fontWeight: 800, fill: INK }}>{es ? "Resumen" : "Summary"}</text>
      <text x="216" y="77" textAnchor="end" style={{ fontSize: 13, fontWeight: 800, fill: INK }}>4.3<tspan style={{ fontSize: 7.5, fill: MUTED, fontWeight: 600 }}>/5</tspan></text>
      {VECTORS(es).map((v, i) => {
        const y = 94 + i * 21;
        return (
          <g key={v}>
            <text x="32" y={y} style={{ fontSize: 7.5, fill: TEXT }}>{v}</text>
            <text x="216" y={y} textAnchor="end" style={{ fontSize: 7.5, fontWeight: 700, fill: INK }}>{scores[i]}<tspan style={{ fill: MUTED, fontWeight: 500 }}>/5</tspan></text>
            <rect x="32" y={y + 4} width="184" height="4" rx="2" fill={NEUTRAL_BG} />
            <rect x="32" y={y + 4} width={184 * scores[i] / 5} height="4" rx="2" fill={BRAND} />
          </g>
        );
      })}
      <line x1="32" y1="173" x2="216" y2="173" stroke={ROW_LINE} strokeWidth="1" />
      <Label x={32} y={186}>{es ? "FORTALEZAS" : "STRENGTHS"}</Label>
      <Bullet x={32} y={198} lines={[es ? "Ha montado operaciones desde cero" : "Built ops from scratch, twice"]} />
      <Label x={32} y={217}>{es ? "A EXPLORAR" : "TO PROBE"}</Label>
      <Bullet x={32} y={229} lines={[es ? "Sería su primer puesto de COO" : "It would be her first COO role"]} />

      {/* Cualificación · Referencias · CV */}
      <rect x="238" y="58" width="382" height="184" rx="12" fill={WHITE} stroke={LINE} strokeWidth="1.5" />
      <Segmented items={sections} active="qual" x={250} y={66} fs={8} h={20} />
      {VECTORS(es).map((v, i) => {
        const y = 103 + i * 35;
        return (
          <g key={v}>
            <text x="250" y={y} style={{ fontSize: 8.5, fontWeight: 800, fill: INK }}>{v}</text>
            <text x="608" y={y} textAnchor="end" style={{ fontSize: 8.5, fontWeight: 700, fill: INK }}>{scores[i]}<tspan style={{ fill: MUTED, fontWeight: 500 }}>/5</tspan></text>
            <Lines x={250} y={y + 11} lines={wrap(why[i], 88)} lh={9.5} style={{ fontSize: 7.5, fill: TEXT }} />
          </g>
        );
      })}
    </Frame>
  );
};

// ─── 4. Evaluación: formulario fijo + tabla por candidato, y la guía ────────
const EVAL_GROUPS = [
  { name: "María Yáñez", rows: [
    { who: "Clara Méndez · COO", s: [3, 3, 2, 3], c: { en: "Strong on ops, ask about P&L", es: "Fuerte en ops; preguntar P&L" } },
    { who: "Sarah Wilkins · CFO", s: [3, 2, 3, 2], c: { en: "Very clear on numbers", es: "Muy clara con los números" } },
  ] },
  { name: "Sofía Pérez", rows: [
    { who: "Clara Méndez · COO", s: [2, 3, 2, 2], c: { en: "Advisor more than operator", es: "Más asesora que operadora" } },
    { who: "Sarah Wilkins · CFO", s: [2, 2, 2, 3], c: { en: "Great growth story", es: "Gran recorrido" } },
  ] },
];
const pctOf = (s) => (s.reduce((a, b) => a + b, 0) / (s.length * 3)) * 100;

const EvalScreen = ({ photos = {}, lang }) => {
  const es = lang === "es";
  const short = es ? ["TÉC.", "SOFT", "CULT.", "POT."] : ["TECH", "SOFT", "CULT", "GROW"];
  const colX = [372, 398, 424, 450];
  const picked = [3, 3, 2, 3];
  let y = 76;
  const rows = [];
  EVAL_GROUPS.forEach((g, gi) => {
    const avg = g.rows.reduce((a, r) => a + pctOf(r.s), 0) / g.rows.length;
    const gy = y;
    rows.push(
      <g key={g.name}>
        <rect x="227" y={gy} width="392" height="24" fill={SURFACE} />
        <SvgAvatar cx={246} cy={gy + 12} r={8} photo={photos[g.name] || null} name={g.name} paletteIndex={gi * 3} clipId={`onb-eval-${gi}`} />
        <text x="260" y={gy + 15} style={{ fontSize: 8.5, fontWeight: 700, fill: INK }}>{g.name}</text>
        <rect x="470" y={gy + 5} width="84" height="14" rx="7" fill={BRAND_LIGHT} />
        <text x="512" y={gy + 14.5} textAnchor="middle" style={{ fontSize: 7, fontWeight: 700, fill: BRAND }}>{es ? "Guía de entrevista" : "Interview guide"}</text>
        <PctChip cx={592} cy={gy + 12} pct={avg} w={38} />
      </g>
    );
    y += 24;
    g.rows.forEach((r, ri) => {
      const ry = y;
      rows.push(
        <g key={`${g.name}-${ri}`}>
          <text x="260" y={ry + 13} style={{ fontSize: 7.5, fill: TEXT }}>{r.who}</text>
          {r.s.map((v, k) => (
            <text key={k} x={colX[k]} y={ry + 13} textAnchor="middle" style={{ fontSize: 8, fontWeight: 700, fill: INK }}>{v}</text>
          ))}
          <text x="470" y={ry + 13} style={{ fontSize: 7, fill: MUTED }}>{es ? r.c.es : r.c.en}</text>
          <PctChip cx={592} cy={ry + 10} pct={pctOf(r.s)} w={32} />
          <line x1="227" y1={ry + 20} x2="619" y2={ry + 20} stroke={ROW_LINE} strokeWidth="1" />
        </g>
      );
      y += 20;
    });
    y += 4;
  });
  return (
    <g>
      {/* Formulario fijo a la izquierda */}
      <rect x="20" y="56" width="196" height="186" rx="12" fill={WHITE} stroke={LINE} strokeWidth="1.5" />
      <text x="32" y="73" style={{ fontSize: 9.5, fontWeight: 800, fill: INK }}>{es ? "Puntuar una entrevista" : "Score an interview"}</text>
      <Label x={32} y={88}>{es ? "CANDIDATO" : "CANDIDATE"}</Label>
      <rect x="32" y="92" width="172" height="17" rx="5" fill={WHITE} stroke={LINE} strokeWidth="1" />
      <text x="39" y="103.5" style={{ fontSize: 8, fill: INK }}>María Yáñez</text>
      <Chevron x={192} y={99} />
      <Label x={32} y={122}>{es ? "ENTREVISTADOR" : "INTERVIEWER"}</Label>
      <rect x="32" y="126" width="172" height="17" rx="5" fill={WHITE} stroke={LINE} strokeWidth="1" />
      <text x="39" y="137.5" style={{ fontSize: 8, fill: INK }}>Clara Méndez · COO</text>
      <Chevron x={192} y={133} />
      {VECTORS(es).map((v, i) => {
        const cy = 158 + i * 17;
        return (
          <g key={v}>
            <text x="32" y={cy + 3} style={{ fontSize: 7.5, fill: TEXT }}>{v}</text>
            {[1, 2, 3].map((n) => {
              const on = picked[i] === n;
              const bx = 152 + (n - 1) * 18;
              return (
                <g key={n}>
                  <rect x={bx} y={cy - 7} width="14" height="14" rx="4" fill={on ? BRAND_LIGHT : WHITE} stroke={on ? BRAND : LINE} strokeWidth="1" />
                  <text x={bx + 7} y={cy + 3} textAnchor="middle" style={{ fontSize: 7.5, fontWeight: 700, fill: on ? BRAND : MUTED }}>{n}</text>
                </g>
              );
            })}
          </g>
        );
      })}
      <rect x="32" y="221" width="172" height="15" rx="6" fill={BRAND} />
      <text x="118" y="231.5" textAnchor="middle" style={{ fontSize: 8, fontWeight: 700, fill: WHITE }}>{es ? "Guardar" : "Save"}</text>

      {/* Tabla agrupada por candidato */}
      <rect x="226" y="56" width="394" height="186" rx="12" fill={WHITE} stroke={LINE} strokeWidth="1.5" />
      <Label x={238} y={70}>{es ? "CANDIDATO" : "CANDIDATE"}</Label>
      {short.map((s, k) => <Label key={s} x={colX[k]} y={70} anchor="middle">{s}</Label>)}
      <Label x={470} y={70}>{es ? "COMENTARIO" : "COMMENT"}</Label>
      <Label x={592} y={70} anchor="middle">%</Label>
      {rows}
    </g>
  );
};

const VisualEvaluation = ({ photos, lang }) => (
  <Frame>
    <PosHeader lang={lang} active="evaluation" />
    <EvalScreen photos={photos} lang={lang} />
  </Frame>
);

const VisualGuide = ({ photos, lang }) => {
  const es = lang === "es";
  const blocks = [
    { t: "label", s: es ? "1. PARA EMPEZAR" : "1. OPENING" },
    { t: "bullet", s: es ? "Presenta a quien entrevista y cómo será la hora." : "Introduce the panel and how the hour will run." },
    { t: "sub", s: es ? "Rompehielos" : "Ice breakers" },
    { t: "bullet", s: es ? "Su vuelta de Londres a Madrid." : "Her move from London back to Madrid." },
    { t: "label", s: "2. TECHNICAL FIT" },
    { t: "q", s: es ? "Cuéntame la última área de operaciones que montaste desde cero." : "Walk me through the last ops function you built from scratch." },
    { t: "listen", s: es ? "Escucha: cifras concretas, decisiones suyas, qué cambiaría." : "Listen for: concrete numbers, her own decisions, what she'd redo." },
    { t: "label", s: "3. SOFT SKILLS" },
    { t: "q", s: es ? "Háblame de una decisión que tu equipo cuestionó." : "Tell me about a decision your team pushed back on." },
  ];
  const X = 382;
  let y = 93;
  const items = blocks.map((b, i) => {
    if (b.t === "label") {
      if (i > 0) y += 5;
      const el = <Label key={i} x={X} y={y}>{b.s}</Label>;
      y += 11;
      return el;
    }
    if (b.t === "sub") {
      const el = <text key={i} x={X} y={y} style={{ fontSize: 7, fill: MUTED }}>{b.s}</text>;
      y += 10;
      return el;
    }
    const lines = wrap(b.s, b.t === "q" ? 48 : 52);
    const el = b.t === "bullet"
      ? <Bullet key={i} x={X} y={y} lines={lines} lh={9.5} />
      : <Lines key={i} x={X} y={y} lines={lines} lh={9.5}
          style={b.t === "q" ? { fontSize: 8, fontWeight: 700, fill: INK } : { fontSize: 7.5, fill: TEXT }} />;
    y += lines.length * 9.5 + 2;
    return el;
  });
  return (
    <Frame>
      <PosHeader lang={lang} active="evaluation" />
      <EvalScreen photos={photos} lang={lang} />
      {/* Panel lateral: tapa la tabla, nunca el formulario */}
      <rect x="371" y="53" width="252" height="194" rx="12" fill={INK} opacity="0.05" />
      <rect x="368" y="50" width="252" height="194" rx="12" fill={WHITE} stroke={LINE} strokeWidth="1.5" />
      <text x={X} y="68" style={{ fontSize: 9.5, fontWeight: 800, fill: INK }}>{es ? "Guía de entrevista" : "Interview guide"}</text>
      <text x={X} y="79" style={{ fontSize: 7.5, fill: MUTED }}>María Yáñez</text>
      <path d="M600,62 l6,6 M606,62 l-6,6" stroke={MUTED} strokeWidth="1.2" strokeLinecap="round" />
      {items}
    </Frame>
  );
};

// Entrevista a pantalla completa: un candidato, un entrevistador; por vector,
// las preguntas de la guía con su línea de notas junto a la puntuación
const VisualInterview = ({ photos = {}, lang }) => {
  const es = lang === "es";
  const scoreRow = (y, label, on) => (
    <g key={label}>
      <text x="424" y={y} style={{ fontSize: 7.5, fill: TEXT }}>{label}</text>
      {[1, 2, 3].map((n) => {
        const bx = 424 + (n - 1) * 62;
        const sel = n === on;
        return (
          <g key={n}>
            <rect x={bx} y={y + 5} width="56" height="16" rx="5" fill={sel ? BRAND_LIGHT : WHITE} stroke={sel ? BRAND : LINE} strokeWidth="1" />
            <text x={bx + 28} y={y + 16} textAnchor="middle" style={{ fontSize: 8, fontWeight: 700, fill: sel ? BRAND : TEXT }}>{n}</text>
          </g>
        );
      })}
    </g>
  );
  return (
    <Frame>
      {/* Cabecera fija */}
      <rect x="0" y="0" width="640" height="46" rx="14" fill={WHITE} />
      <line x1="0" y1="46" x2="640" y2="46" stroke={LINE} strokeWidth="1" />
      <SvgAvatar cx={34} cy={23} r={12} photo={photos["María Yáñez"] || null} name="María Yáñez" paletteIndex={0} clipId="onb-int-av" />
      <text x="54" y="21" style={{ fontSize: 10, fontWeight: 800, fill: INK }}>María Yáñez</text>
      <text x="54" y="33" style={{ fontSize: 7.5, fill: MUTED }}>{es ? "Director Strategy & Ops · UBS · entrevistador/a: Clara Méndez" : "Director Strategy & Ops · UBS · interviewer: Clara Méndez"}</text>
      <text x="530" y="21" textAnchor="end" style={{ fontSize: 8.5, fontWeight: 700, fill: INK }}>3/8 · 89%</text>
      <text x="530" y="32" textAnchor="end" style={{ fontSize: 7, fill: MUTED }}>{es ? "✓ Guardado" : "✓ Saved"}</text>
      <rect x="540" y="13" width="60" height="20" rx="7" fill={BRAND} />
      <text x="570" y="26.5" textAnchor="middle" style={{ fontSize: 8, fontWeight: 700, fill: WHITE }}>{es ? "Terminar" : "Finish"}</text>
      <path d="M612,19 l7,7 M619,19 l-7,7" stroke={MUTED} strokeWidth="1.2" strokeLinecap="round" />

      {/* Un vector: qué preguntar | puntúa */}
      <rect x="20" y="58" width="600" height="160" rx="12" fill={WHITE} stroke={LINE} strokeWidth="1.5" />
      <text x="34" y="76" style={{ fontSize: 8, fontWeight: 800, fill: BRAND, letterSpacing: 0.5 }}>TECHNICAL FIT</text>
      <text x="606" y="76" textAnchor="end" style={{ fontSize: 7.5, fill: MUTED }}>{es ? "1/2 puntuadas" : "1/2 scored"}</text>
      <line x1="20" y1="84" x2="620" y2="84" stroke={ROW_LINE} strokeWidth="1" />
      <rect x="410" y="85" width="209" height="132" fill={SURFACE} />
      <line x1="410" y1="84" x2="410" y2="217" stroke={ROW_LINE} strokeWidth="1" />
      <Label x={34} y={98}>{es ? "QUÉ PREGUNTAR" : "WHAT TO ASK"}</Label>
      <Lines x={34} y={112} lh={10} lines={wrap(es ? "Cuéntame la última área de operaciones que montaste desde cero." : "Walk me through the last ops function you built from scratch.", 64)} style={{ fontSize: 8, fontWeight: 700, fill: INK }} />
      <text x="34" y="133" style={{ fontSize: 7.5, fill: MUTED }}>{es ? "Escucha: cifras concretas, decisiones suyas." : "Listen for: concrete numbers, her own decisions."}</text>
      <rect x="34" y="139" width="362" height="17" rx="5" fill={WHITE} stroke={LINE} strokeWidth="1" />
      <text x="41" y="150.5" style={{ fontSize: 7.5, fill: INK }}>{es ? "Montó ops en Revolut ES: 3 a 40 personas en 18 meses" : "Built ops at Revolut ES: 3 to 40 people in 18 months"}</text>
      <Lines x={34} y={172} lh={10} lines={wrap(es ? "¿Qué decisión de presupuesto te costó más defender?" : "Which budget decision was hardest to defend?", 64)} style={{ fontSize: 8, fontWeight: 700, fill: INK }} />
      <rect x="34" y="184" width="362" height="17" rx="5" fill={WHITE} stroke={LINE} strokeWidth="1" />
      <text x="41" y="195.5" style={{ fontSize: 7.5, fill: FAINT }}>{es ? "Apunta lo que dice…" : "Note what they say…"}</text>
      <Label x={424} y={98}>{es ? "PUNTÚA" : "SCORE"}</Label>
      {scoreRow(112, es ? "Me da confianza en su capacidad" : "Gives me confidence in ability", 3)}
      {scoreRow(152, es ? "Lo ha hecho durante años" : "Has done this for many years", 0)}

      <rect x="20" y="228" width="600" height="24" rx="12" fill={WHITE} stroke={LINE} strokeWidth="1.5" />
      <text x="34" y="244" style={{ fontSize: 8, fontWeight: 800, fill: BRAND, letterSpacing: 0.5 }}>SOFT SKILLS</text>
    </Frame>
  );
};

// ─── 5. Referencias: quién habla y en qué coinciden ─────────────────────────
const VisualReferences = ({ photos = {}, lang }) => {
  const es = lang === "es";
  const sections = [["cv", "CV"], ["qual", es ? "Cualificación" : "Qualification"], ["refs", es ? "Referencias" : "References"]];
  const refs = [
    { n: "Javier Ruiz", r: es ? "Su jefe anterior · UBS" : "Former manager · UBS", done: true },
    { n: "Elena Castro", r: es ? "Compañera · Head of Strategy" : "Peer · Head of Strategy", done: true },
    { n: "Marta Gil", r: es ? "Le reportaba · Ops Manager" : "Direct report · Ops Manager", done: false },
  ];
  const agree = es
    ? ["Tranquila y estructurada bajo presión.", "Pone orden rápido sin frenar al equipo.", "Hace crecer a su gente: dos ascensos en su equipo."]
    : ["Calm and structured under pressure.", "Builds process fast without slowing the team.", "Develops people: two of her reports promoted."];
  return (
    <Frame>
      <ProfileHeader lang={lang} active="report" photo={photos["María Yáñez"] || null} />

      <rect x="20" y="58" width="290" height="184" rx="12" fill={WHITE} stroke={LINE} strokeWidth="1.5" />
      <Segmented items={sections} active="refs" x={32} y={66} fs={8} h={20} />
      {refs.map((r, i) => {
        const y = 100 + i * 34;
        return (
          <g key={r.n}>
            {i > 0 && <line x1="32" y1={y - 8} x2="298" y2={y - 8} stroke={ROW_LINE} strokeWidth="1" />}
            <SvgAvatar cx={44} cy={y + 6} r={10} photo={null} name={r.n} paletteIndex={i + 2} clipId={`onb-ref-${i}`} />
            <text x="60" y={y + 4} style={{ fontSize: 8.5, fontWeight: 700, fill: INK }}>{r.n}</text>
            <text x="60" y={y + 15} style={{ fontSize: 7.5, fill: MUTED }}>{r.r}</text>
            <Chip x={238} y={y - 1} w={60} bg={NEUTRAL_BG} fg={r.done ? TEXT : MUTED}>
              {r.done ? (es ? "Recibida" : "Received") : (es ? "Pedida" : "Requested")}
            </Chip>
          </g>
        );
      })}
      <text x="32" y="228" style={{ fontSize: 8, fontWeight: 700, fill: BRAND }}>{es ? "+ Pedir referencia" : "+ Request reference"}</text>

      <rect x="320" y="58" width="300" height="184" rx="12" fill={WHITE} stroke={LINE} strokeWidth="1.5" />
      <text x="334" y="77" style={{ fontSize: 9.5, fontWeight: 800, fill: INK }}>{es ? "En qué coinciden" : "Where they agree"}</text>
      <text x="334" y="89" style={{ fontSize: 7.5, fill: MUTED }}>{es ? "2 de 2 referencias recibidas" : "2 of 2 references received"}</text>
      {agree.map((a, i) => <Bullet key={a} x={334} y={107 + i * 13} lines={[a]} />)}
      <Label x={334} y={158}>{es ? "A COMPROBAR ANTES DE DECIDIR" : "TO CHECK BEFORE DECIDING"}</Label>
      <Bullet x={334} y={171} lines={[es ? "Puede ser demasiado operativa los primeros meses." : "Can stay too hands-on in the first months."]} />
      <rect x="334" y="184" width="272" height="46" rx="8" fill={SURFACE} />
      <text x="346" y="202" style={{ fontSize: 8.5, fill: INK, fontStyle: "italic" }}>
        {es ? "“Sería mi primera llamada para darle la vuelta a un área.”" : "“She'd be my first call to turn a business unit around.”"}
      </text>
      <text x="346" y="218" style={{ fontSize: 7.5, fill: MUTED }}>{es ? "Javier Ruiz, su jefe anterior" : "Javier Ruiz, former manager"}</text>
    </Frame>
  );
};

// ─── 6. El candidato sabe en qué punto está: Process → portal ───────────────
const VisualCandidateSide = ({ photos = {}, lang }) => {
  const es = lang === "es";
  const steps = es
    ? ["Recibiendo candidaturas", "Entrevistas", "Ronda final", "Seleccionado"]
    : ["Receiving applications", "Interview process", "Final round", "Selected"];
  const current = 2;
  const sx = (i) => 300 + i * 92;
  const msg = es
    ? "Ronda final el jueves con el CEO. Mañana te mando una nota para prepararla."
    : "Final round on Thursday with the CEO. I'll send you a prep note tomorrow.";
  return (
    <Frame>
      {/* En Rutment: el campo Process, que solo cambia el admin */}
      <rect x="20" y="62" width="176" height="128" rx="12" fill={WHITE} stroke={LINE} strokeWidth="1.5" />
      <Label x={32} y={80}>{es ? "EN RUTMENT" : "IN RUTMENT"}</Label>
      <SvgAvatar cx={44} cy={100} r={11} photo={photos["María Yáñez"] || null} name="María Yáñez" paletteIndex={0} clipId="onb-side-maria" />
      <text x="62" y="98" style={{ fontSize: 8.5, fontWeight: 700, fill: INK }}>María Yáñez</text>
      <text x="62" y="108" style={{ fontSize: 7, fill: MUTED }}>Chief Operating Officer</text>
      <Label x={32} y={132}>{es ? "PROCESO" : "PROCESS"}</Label>
      <rect x="32" y="137" width="152" height="20" rx="6" fill={WHITE} stroke={LINE} strokeWidth="1" />
      <text x="40" y="150" style={{ fontSize: 8.5, fontWeight: 700, fill: BRAND }}>{steps[current]}</text>
      <Chevron x={172} y={145} />
      <text x="32" y="176" style={{ fontSize: 7, fill: MUTED }}>{es ? "Lo marca Rutment" : "Set by Rutment"}</text>

      <path d="M204,126 h30 M228,121 l6,5 l-6,5" fill="none" stroke={FAINT} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />

      {/* Portal del candidato */}
      <rect x="244" y="14" width="376" height="226" rx="12" fill={WHITE} stroke={LINE} strokeWidth="1.5" />
      <path d="M244,34 v-8 a12,12 0 0 1 12,-12 h352 a12,12 0 0 1 12,12 v8 z" fill={SURFACE} />
      {[0, 1, 2].map((i) => <circle key={i} cx={258 + i * 9} cy={24} r="2.5" fill={LINE} />)}
      <text x="432" y="27" textAnchor="middle" style={{ fontSize: 7, fill: MUTED }}>candidates.rutment.com</text>
      <text x="260" y="54" style={{ fontSize: 10, fontWeight: 800, fill: INK }}>{es ? "Tus procesos" : "Your processes"}</text>
      <rect x="258" y="62" width="348" height="92" rx="10" fill={WHITE} stroke={LINE} strokeWidth="1.5" />
      <text x="272" y="80" style={{ fontSize: 9, fontWeight: 800, fill: INK }}>Chief Operating Officer</text>
      <text x="272" y="91" style={{ fontSize: 7.5, fill: MUTED }}>Matteco · Madrid</text>
      <rect x="506" y="71" width="88" height="18" rx="7" fill={BRAND_LIGHT} />
      <text x="550" y="83" textAnchor="middle" style={{ fontSize: 7.5, fontWeight: 700, fill: BRAND }}>{es ? "Preguntar al recruiter" : "Ask recruiter"}</text>
      {steps.slice(0, -1).map((_, i) => (
        <line key={i} x1={sx(i)} y1={118} x2={sx(i + 1)} y2={118} stroke={i < current ? BRAND : LINE} strokeWidth="2" />
      ))}
      {steps.map((s, i) => (
        <g key={s}>
          {i < current && <circle cx={sx(i)} cy={118} r="5" fill={BRAND} />}
          {i === current && <circle cx={sx(i)} cy={118} r="6" fill={WHITE} stroke={BRAND} strokeWidth="2" />}
          {i === current && <circle cx={sx(i)} cy={118} r="2.5" fill={BRAND} />}
          {i > current && <circle cx={sx(i)} cy={118} r="5" fill={WHITE} stroke={LINE} strokeWidth="2" />}
          <text x={sx(i)} y={138} textAnchor="middle" style={{ fontSize: 7, fontWeight: i === current ? 700 : 500, fill: i === current ? INK : MUTED }}>{s}</text>
        </g>
      ))}

      {/* Chat con el recruiter */}
      <rect x="272" y="164" width="300" height="32" rx="10" fill={NEUTRAL_BG} />
      <Lines x={282} y={177} lines={wrap(msg, 66)} lh={10} style={{ fontSize: 8, fill: INK }} />
      <text x="272" y="206" style={{ fontSize: 6.5, fill: MUTED }}>{es ? "Recruiter · 10:24" : "Recruiter · 10:24"}</text>
      <rect x="466" y="210" width="140" height="20" rx="10" fill={BRAND_LIGHT} />
      <text x="536" y="223" textAnchor="middle" style={{ fontSize: 8, fill: INK }}>{es ? "¡Perfecto, gracias!" : "Perfect, thanks!"}</text>
    </Frame>
  );
};

// ─── Ayudas de cada pestaña de la posición (el "?" de la cabecera, 20/10) ───
// Misma ventana y piezas que el tour. Todo lo que cuentan existe en la
// pantalla: si una pestaña cambia, se cambian sus diapositivas.

// Mapping · aprobar: el rango salarial publicado y la señal para contactar
const VisualMappingApprove = ({ lang }) => {
  const es = lang === "es";
  return (
    <Frame>
      <PosHeader lang={lang} active="mapping" />
      <rect x="20" y="60" width="600" height="56" rx="12" fill={WHITE} stroke={LINE} strokeWidth="1.5" />
      <Label x={36} y={80}>{es ? "RANGO RECOMENDADO" : "RECOMMENDED RANGE"}</Label>
      <text x="36" y="102" style={{ fontSize: 15, fontWeight: 800, fill: INK }}>90–110K + 15–20% bonus</text>
      <text x="604" y="102" textAnchor="end" style={{ fontSize: 8, fill: MUTED }}>{es ? "Lo publica Rutment" : "Published by Rutment"}</text>
      <rect x="20" y="128" width="600" height="56" rx="12" fill={WHITE} stroke={LINE} strokeWidth="1.5" />
      <text x="36" y="151" style={{ fontSize: 10, fontWeight: 800, fill: INK }}>{es ? "Revisa 2 empresas y 1 perfil" : "Review 2 companies and 1 profile"}</text>
      <text x="36" y="166" style={{ fontSize: 8, fill: MUTED }}>{es ? "Cuando estés conforme, aprueba el mapping." : "When you're happy, approve the mapping."}</text>
      <rect x="490" y="144" width="114" height="24" rx="8" fill={BRAND} />
      <text x="547" y="159.5" textAnchor="middle" style={{ fontSize: 9, fontWeight: 700, fill: WHITE }}>{es ? "Aprobar mapping" : "Approve mapping"}</text>
      <text x="320" y="214" textAnchor="middle" style={{ fontSize: 9, fill: TEXT }}>{es ? "Aprobar es la señal para empezar a contactar. Puedes retirar la aprobación." : "Approving is the signal to start contacting. You can withdraw it."}</text>
    </Frame>
  );
};

// Brief: los documentos de la posición (la cultura, como enlace a su página desde el 31/10)
const VisualDocs = ({ lang }) => {
  const es = lang === "es";
  const langs = [["orig", "Original"], ["es", "Español"], ["en", "English"]];
  const doc = (x, title, lines, extra) => (
    <g>
      <rect x={x} y={92} width={190} height={150} rx="12" fill={WHITE} stroke={LINE} strokeWidth="1.5" />
      <text x={x + 14} y={112} style={{ fontSize: 9.5, fontWeight: 800, fill: INK }}>{title}</text>
      <text x={x + 176} y={112} textAnchor="end" style={{ fontSize: 7.5, fontWeight: 700, fill: MUTED }}>PDF</text>
      <line x1={x} y1={122} x2={x + 190} y2={122} stroke={ROW_LINE} />
      {lines.map((w, i) => <rect key={i} x={x + 14} y={134 + i * 12} width={w} height="4" rx="2" fill={LINE} />)}
      {extra}
    </g>
  );
  return (
    <Frame>
      <PosHeader lang={lang} active="documents" />
      <Segmented items={langs} active="orig" x={20} y={60} fs={8} h={20} />
      {doc(20, es ? "Descripción del puesto" : "Job description", [150, 160, 120, 155, 90, 140],
        <text x={34} y={230} style={{ fontSize: 8, fontWeight: 700, fill: BRAND }}>{es ? "Pedir cambio" : "Request change"}</text>)}
      {doc(225, es ? "Cuestionario" : "Questionnaire", [140, 120, 160, 100, 150, 130])}
      {doc(430, es ? "Cultura de la empresa" : "Company culture", [],
        <text x={444} y={150} style={{ fontSize: 8, fontWeight: 700, fill: BRAND }}>{es ? "Ver Culture statement →" : "Open Culture statement →"}</text>)}
    </Frame>
  );
};

// ─── Documento de cultura (31/10) ──────────────────────────────────────────
// Ilustraciones del «?» de Culture statement: el hexágono, los dos extremos de
// una dimensión (ninguno es mejor), el uso con candidatos y la base académica.
const CULT_DEMO = [["speed", 4.5], ["autonomy", 4], ["standards", 3.5], ["growth", 3], ["candor", 4], ["risk", 2.5]];
const VisualCultureHex = ({ lang }) => {
  const es = lang === "es";
  const names = CULTURE_DIMS.reduce((o, d) => ({ ...o, [d.id]: es ? d.es : d.en }), {});
  const cx = 320, cy = 128, R = 88;
  const pt = (i, r) => { const a = -Math.PI / 2 + (i * Math.PI) / 3; return [cx + r * Math.cos(a), cy + r * Math.sin(a)]; };
  const ring = (r) => CULT_DEMO.map((_, i) => pt(i, r).join(",")).join(" ");
  const val = CULT_DEMO.map(([, s], i) => pt(i, (R * (s - 1)) / 4));
  return (
    <Frame>
      {[5, 4, 3, 2].map(k => <polygon key={k} points={ring((R * (k - 1)) / 4)} fill={k === 5 ? SURFACE : "none"} stroke={LINE} />)}
      <polygon points={val.map(p => p.join(",")).join(" ")} fill={CULTURE_COLORS.speed} fillOpacity="0.6" stroke={ARCH_COLORS[1]} strokeWidth="2" strokeLinejoin="round" />
      {CULT_DEMO.map(([id, s], i) => {
        const [x, y] = pt(i, R + 18);
        const anchor = Math.abs(x - cx) < 4 ? "middle" : x > cx ? "start" : "end";
        return (
          <g key={id}>
            <text x={x} y={y - 2} textAnchor={anchor} style={{ fontSize: 12, fontWeight: 800, fill: INK }}>{s.toFixed(1)}</text>
            <text x={x} y={y + 11} textAnchor={anchor} style={{ fontSize: 9, fill: MUTED }}>{names[id]}</text>
          </g>
        );
      })}
    </Frame>
  );
};
const VisualCultureEnds = ({ lang }) => {
  const es = lang === "es";
  const rows = CULTURE_DIMS.slice(0, 3);
  return (
    <Frame>
      {rows.map((d, i) => {
        const y = 44 + i * 66;
        const dot = [0.8, 0.35, 0.6][i];
        return (
          <g key={d.id}>
            <text x={30} y={y} style={{ fontSize: 10, fontWeight: 800, fill: INK }}>{es ? d.es : d.en}</text>
            <rect x={30} y={y + 10} width={580} height={8} rx="4" fill={NEUTRAL_BG} />
            <circle cx={30 + 580 * dot} cy={y + 14} r="8" fill={CULTURE_COLORS[d.id]} stroke={WHITE} strokeWidth="2" />
            <text x={30} y={y + 34} style={{ fontSize: 9, fill: TEXT }}>{es ? d.low.es : d.low.en}</text>
            <text x={610} y={y + 34} textAnchor="end" style={{ fontSize: 9, fill: TEXT }}>{es ? d.high.es : d.high.en}</text>
          </g>
        );
      })}
      <text x={320} y={240} textAnchor="middle" style={{ fontSize: 9.5, fontWeight: 700, fill: MUTED }}>{es ? "Los dos extremos funcionan: depende de la empresa y del puesto" : "Both ends work: it depends on the company and the role"}</text>
    </Frame>
  );
};
const VisualCultureFit = ({ lang }) => {
  const es = lang === "es";
  const cand = (x, title, sub, fill) => (
    <g>
      <rect x={x} y={70} width={170} height={110} rx="12" fill={WHITE} stroke={LINE} strokeWidth="1.5" />
      <circle cx={x + 85} cy={104} r="18" fill={fill} />
      <text x={x + 85} y={142} textAnchor="middle" style={{ fontSize: 10, fontWeight: 800, fill: INK }}>{title}</text>
      <text x={x + 85} y={158} textAnchor="middle" style={{ fontSize: 8.5, fill: MUTED }}>{sub}</text>
    </g>
  );
  return (
    <Frame>
      <rect x={30} y={50} width={190} height={150} rx="12" fill={WHITE} stroke={LINE} strokeWidth="1.5" />
      <text x={44} y={72} style={{ fontSize: 10, fontWeight: 800, fill: INK }}>{es ? "Documento de cultura" : "Culture statement"}</text>
      <line x1={30} y1={84} x2={220} y2={84} stroke={ROW_LINE} />
      {[150, 160, 120, 155, 100, 140, 90].map((w, i) => <rect key={i} x={44} y={96 + i * 13} width={w} height="4" rx="2" fill={LINE} />)}
      <text x={250} y={130} style={{ fontSize: 22, fill: FAINT }}>→</text>
      {cand(280, es ? "Encaja" : "Fits", es ? "llega convencido" : "arrives convinced", ARCH_QUAD_FILLS[3])}
      {cand(460, es ? "No encaja" : "Doesn't fit", es ? "se autoexcluye" : "self-selects out", NEUTRAL_BG)}
      <text x={455} y={222} textAnchor="middle" style={{ fontSize: 9.5, fontWeight: 700, fill: MUTED }}>{es ? "El encaje se puntúa en el vector Cultural fit" : "Fit is scored in the Cultural fit vector"}</text>
    </Frame>
  );
};
const VisualCultureResearch = ({ lang }) => {
  const es = lang === "es";
  const src = [["O'Reilly et al.", "1991"], ["Hofstede et al.", "1990"], ["Eisenhardt", "1989"], ["Edmondson", "1999"], ["Gosling et al.", "2003"]];
  return (
    <Frame>
      {src.map(([who, y], i) => (
        <g key={who}>
          <rect x={22 + i * 122} y={70} width={110} height={90} rx="12" fill={WHITE} stroke={LINE} strokeWidth="1.5" />
          <text x={77 + i * 122} y={104} textAnchor="middle" style={{ fontSize: 9.5, fontWeight: 800, fill: INK }}>{who}</text>
          <text x={77 + i * 122} y={122} textAnchor="middle" style={{ fontSize: 9, fill: MUTED }}>{y}</text>
          {[60, 76, 50].map((w, k) => <rect key={k} x={77 + i * 122 - w / 2} y={134 + k * 7} width={w} height="3" rx="1.5" fill={LINE} />)}
        </g>
      ))}
      <text x={320} y={200} textAnchor="middle" style={{ fontSize: 9.5, fontWeight: 700, fill: MUTED }}>{es ? "Investigación publicada y revisada por pares · cálculo siempre igual" : "Published, peer-reviewed research · always calculated the same way"}</text>
    </Frame>
  );
};

const TAB_HELP_TITLE = {
  culture: ["Cómo funciona el documento de cultura", "How the Culture statement works"],
  mapping: ["Cómo funciona el Mapping", "How Mapping works"],
  candidates: ["Cómo funcionan los Candidatos", "How Candidates works"],
  evaluation: ["Cómo funciona la Evaluación", "How Evaluation works"],
  documents: ["Cómo funciona el Brief", "How the Brief works"],
};

const buildTabHelp = (tab, photos, lang = "en") => {
  const es = lang === "es";
  const got = es ? "Entendido" : "Got it";
  const sets = {
    culture: [
      { title: es ? "Cómo trabaja tu empresa, en 6 dimensiones" : "How your company works, in 6 dimensions",
        body: es ? "14 preguntas sobre situaciones reales dan una nota de 1 a 5 en velocidad, autonomía, exigencia, desarrollo de personas, franqueza y largo plazo." : "14 questions about real situations give a 1-to-5 score on speed, autonomy, standards, people development, candor and long term.",
        visual: <VisualCultureHex lang={lang} /> },
      { title: es ? "Ningún resultado es bueno ni malo" : "No result is good or bad",
        body: es ? "Cada dimensión tiene dos extremos y los dos funcionan. Un 1 no es peor que un 5: el perfil solo indica una forma de hacer las cosas." : "Each dimension has two ends and both work. A 1 is not worse than a 5: the profile only shows one way of doing things.",
        visual: <VisualCultureEnds lang={lang} /> },
      { title: es ? "Sirve para el encaje con cada candidato" : "It's for fit with each candidate",
        body: es ? "El documento se comparte antes de la entrevista: quien no encaja se autoexcluye, y quien encaja llega convencido." : "The statement is shared before the interview: those who don't fit self-select out, and those who do arrive convinced.",
        visual: <VisualCultureFit lang={lang} /> },
      { title: es ? "Con base académica" : "Grounded in research",
        body: es ? "Las dimensiones salen de investigación publicada y se calculan siempre igual. Los detalles, en la pestaña Metodología." : "The dimensions come from published research and are always calculated the same way. Details in the Methodology tab.",
        visual: <VisualCultureResearch lang={lang} />, cta: got },
    ],
    mapping: [
      { title: es ? "Validas el mercado antes de que contactemos a nadie" : "You validate the market before we contact anyone",
        body: es ? "Te proponemos las empresas objetivo. Valida (✓), veta (✕, con motivo), comenta o propón otras." : "We propose the target companies. Validate (✓), veto (✕, with a reason), comment or suggest others.",
        visual: <VisualMappingCompanies lang={lang} /> },
      { title: es ? "Perfiles sin nombre" : "Profiles without names",
        body: es ? "Cada perfil se describe sin nombre ni empresa actual. Valídalo o vétalo: así afinamos a quién llamar." : "Each profile is described without name or current company. Validate or veto it: that's how we fine-tune who to call.",
        visual: <VisualMappingProfiles lang={lang} /> },
      { title: es ? "Aprobar el mapping" : "Approve the mapping",
        body: es ? "Cuando estés conforme, Aprobar mapping: es la señal para empezar a contactar. Si Rutment publica un rango salarial, sale arriba." : "When you're happy, Approve mapping: it's the signal to start contacting. If Rutment publishes a salary range, it shows at the top.",
        visual: <VisualMappingApprove lang={lang} />, cta: got },
    ],
    candidates: [
      { title: es ? "El Tablero: la fase la dice la columna" : "The Board: the column is the phase",
        body: es ? "Los pendientes son lo que te toca decidir: Validar (con o sin correo para agendar entrevista) o Rechazar." : "Pending candidates are yours to decide: Validate (with or without an email to book an interview) or Reject.",
        visual: <VisualBoard photos={photos} lang={lang} /> },
      { title: es ? "La ficha: cada nota con su porqué" : "The profile: every score with its why",
        body: es ? "Pulsa un candidato: los 4 vectores puntuados con su justificación, la cualificación y el CV." : "Click a candidate: the 4 vectors scored with their rationale, the qualification and the CV.",
        visual: <VisualReport photos={photos} lang={lang} /> },
      { title: es ? "Referencias y comparar" : "References and compare",
        body: es ? "En la ficha también ves las referencias y en qué coinciden, y puedes comparar al candidato con los demás." : "The profile also shows the references and where they agree, and you can compare the candidate with the rest.",
        visual: <VisualReferences photos={photos} lang={lang} />, cta: got },
    ],
    evaluation: [
      { title: es ? "Evalúa cada entrevista" : "Score every interview",
        body: es ? "«Evaluar una entrevista» abre la pantalla completa: eliges candidato y entrevistador, y por cada vector tienes a la izquierda qué preguntar y a la derecha la puntuación de 1 a 3, con notas. Se guarda solo." : "“Evaluate an interview” opens full screen: pick candidate and interviewer, and for each vector you get what to ask on the left and the 1-to-3 score on the right, with notes. It saves itself.",
        visual: <VisualInterview photos={photos} lang={lang} /> },
      { title: es ? "Una guía de entrevista por candidato" : "An interview guide per candidate",
        body: es ? "Preguntas sobre su experiencia real, con qué escuchar, calibradas contra lo que la cualificación dejó por aclarar." : "Questions about their real experience, with what to listen for, calibrated against what qualification left open.",
        visual: <VisualGuide photos={photos} lang={lang} /> },
      { title: es ? "La tabla: el % de cada candidato" : "The table: each candidate's %",
        body: es ? "Cada evaluación suma al % del candidato, por entrevistador y en total. Así comparas con evidencias, no de memoria. Desde la tabla puedes editar o borrar una evaluación." : "Each evaluation adds to the candidate's %, per interviewer and overall. So you compare on evidence, not from memory. From the table you can edit or delete an evaluation.",
        visual: <VisualEvaluation photos={photos} lang={lang} />, cta: got },
    ],
    documents: [
      { title: es ? "El brief de la posición" : "The position's brief",
        body: es ? "Descripción del puesto y cuestionario de cualificación, con PDF y traducción Original / Español / English. La cultura de la empresa está en su Culture statement." : "Job description and qualification questionnaire, with PDF and Original / Spanish / English translation. Your company culture lives in its Culture statement.",
        visual: <VisualDocs lang={lang} /> },
      { title: es ? "El brief es la vara de medir" : "The brief is the yardstick",
        body: es ? "Con él se puntúa a cada candidato. Si algo no encaja, Pedir cambio en la descripción del puesto." : "Every candidate is scored against it. If something is off, Request change on the job description.",
        visual: <VisualBrief lang={lang} />, cta: got },
    ],
  };
  return sets[tab] || [];
};

// ─── Slides ────────────────────────────────────────────────────────────────
// El recorrido es el de la metodología (CLAUDE.md, "LA METODOLOGÍA"): brief →
// mapping (paso previo, antes de contactar a nadie) → las 3 fases
// (Cualificación, Evaluación, Referencias) → el candidato informado. Cada
// texto responde a qué deja de hacer el founder, no a qué hace el producto.
//
// El PRECIO no entra aquí a propósito: dicho antes de ver el método posiciona
// como barato en vez de como bueno.
// Rework (11/10): una frase por diapositiva; la ilustración cuenta el resto.
const buildSlides = (photos, lang = "en") => {
  const es = lang === "es";
  return [
  {
    title: es ? "Contrata como si tuvieras un equipo de selección" : "Hire like you had a recruiting team",
    body: es ? "Executive search para founders: nosotros buscamos y filtramos; tú decides con evidencias." : "Executive search for founders: we search and filter; you decide on evidence.",
    visual: <VisualWelcome />,
  },
  {
    title: es ? "Convertimos un encargo difuso en criterios reales" : "We turn a vague brief into real criteria",
    body: es ? "Fijamos el puesto y cómo trabajáis: la vara con la que se mide a cada candidato." : "We pin down the role and how you work: the yardstick for every candidate.",
    visual: <VisualBrief lang={lang} />,
  },
  {
    title: es ? "Validas el mercado antes de que llamemos a nadie" : "You validate the market before we call anyone",
    body: es ? "Validas empresas y perfiles anónimos antes de que contactemos a nadie." : "You approve target companies and anonymous profiles before we contact anyone.",
    visuals: [<VisualMappingCompanies lang={lang} />, <VisualMappingProfiles lang={lang} />],
  },
  {
    title: es ? "Solo ves a quien merece tu tiempo" : "You only see the ones worth your time",
    body: es ? "Cada candidato llega puntuado en 4 vectores, con el porqué de cada nota." : "Every candidate arrives scored on 4 vectors, with the why behind each score.",
    visuals: [<VisualBoard photos={photos} lang={lang} />, <VisualReport photos={photos} lang={lang} />],
  },
  {
    title: es ? "Tu equipo entrevista con evidencias, no por intuición" : "Your team interviews on evidence, not gut feeling",
    body: es ? "Una guía por candidato: todo tu equipo entrevista y puntúa igual." : "One guide per candidate: your whole team interviews and scores the same way.",
    visuals: [<VisualEvaluation photos={photos} lang={lang} />, <VisualGuide photos={photos} lang={lang} />],
  },
  {
    title: es ? "Referencias: la última comprobación antes de decidir" : "References: the final check before you decide",
    body: es ? "Hablamos con quienes han trabajado con esa persona antes de la oferta, no después." : "We talk to people who worked with them before the offer, not after.",
    visual: <VisualReferences photos={photos} lang={lang} />,
  },
  {
    title: es ? "El candidato siempre sabe en qué punto está" : "The candidate always knows where they stand",
    body: es ? "El candidato ve su fase y puede escribirnos: por eso dicen que sí los buenos." : "Candidates see their stage and can message us: that is why the good ones say yes.",
    visual: <VisualCandidateSide photos={photos} lang={lang} />,
    cta: es ? "Crear mi primera posición" : "Create my first position",
  },
  ];
};

// ─── Main component ─────────────────────────────────────────────────────────

const PHOTO_NAMES = ["María Yáñez", "Sofía Pérez", "Beatriz López", "Andrés Molina", "Laura Ibáñez", "Pablo Ortega"];

// help: una pestaña de la posición ("mapping", "candidates", "evaluation",
// "documents") abre su ayuda en vez del tour.
export default function OnboardingModal({ onClose, onCTA, lang = "en", help = null }) {
  const [step, setStep] = useState(0);
  const [photos, setPhotos] = useState({});
  const [visualIdx, setVisualIdx] = useState(0);
  const [dragStartX, setDragStartX] = useState(null);
  const isMobile = useIsMobile();

  // Reset sub-visual index when step changes
  useEffect(() => { setVisualIdx(0); setDragStartX(null); }, [step]);

  // Escape cierra el tour (como Saltar tour)
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(true); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Load candidate photos from Supabase (RLS-friendly: silently fails to fallback)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: cands } = await supabase
          .from("candidates")
          .select("id, name")
          .in("name", PHOTO_NAMES);
        if (!cands || cands.length === 0 || cancelled) return;
        const ids = cands.map((c) => c.id);
        const { data: photosData } = await supabase
          .from("candidate_photos")
          .select("candidate_id, photo_url")
          .in("candidate_id", ids);
        if (!photosData || cancelled) return;
        const map = {};
        cands.forEach((c) => {
          const p = photosData.find((ph) => ph.candidate_id === c.id);
          if (p) map[c.name] = p.photo_url;
        });
        if (!cancelled) setPhotos(map);
      } catch (err) {
        console.error("Onboarding: error loading candidate photos:", err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const slides = help ? buildTabHelp(help, photos, lang) : buildSlides(photos, lang);
  const modalTitle = help ? (lang === "es" ? TAB_HELP_TITLE[help][0] : TAB_HELP_TITLE[help][1]) : (lang === "es" ? "Cómo funciona" : "How it works");
  const slide = slides[step];
  const isLast = step === slides.length - 1;
  const isFirst = step === 0;

  const handleNext = () => {
    if (isLast) {
      onClose(true);
      if (onCTA) onCTA();
    } else {
      setStep(step + 1);
    }
  };

  const handleBack = () => { if (!isFirst) setStep(step - 1); };
  const handleSkip = () => onClose(true);

  return (
    <div style={{
      position: "fixed", inset: 0, background: SCRIM,
      zIndex: 3000, display: "flex", alignItems: "center", justifyContent: "center",
      padding: isMobile ? 0 : 20,
    }}>
      <div style={isMobile ? {
        // Móvil: pantalla completa, cabecera propia y botones fijos abajo
        position: "fixed", inset: 0, background: WHITE, height: "100dvh",
        display: "flex", flexDirection: "column", overflow: "hidden",
        paddingTop: "env(safe-area-inset-top)", boxSizing: "border-box",
      } : {
        background: WHITE, borderRadius: 16, width: "100%", maxWidth: 760,
        height: 620,
        boxShadow: SHADOW_MODAL,
        display: "flex", flexDirection: "column", overflow: "hidden",
        position: "relative",
      }}>

        {isMobile && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "8px 12px 8px 16px", borderBottom: `1px solid ${ROW_LINE}`, flexShrink: 0 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: INK }}>{modalTitle}</div>
              <div style={{ fontSize: 12, color: MUTED }}>{step + 1} / {slides.length}</div>
            </div>
            <button onClick={handleSkip} aria-label={lang === "es" ? "Cerrar" : "Close"}
              style={{ width: 40, height: 40, borderRadius: "50%", border: "none", background: NEUTRAL_BG, color: TEXT, fontSize: 16, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontFamily: "inherit" }}>
              ✕
            </button>
          </div>
        )}

        {/* Móvil: ilustración y texto en una zona con scroll propio. En
            escritorio el envoltorio no cuenta para la maquetación (contents). */}
        <div style={isMobile ? { flex: 1, minHeight: 0, overflowY: "auto", WebkitOverflowScrolling: "touch" } : { display: "contents" }}>

        {/* Visual area — fixed height, with drag/swipe + arrows + dots if multi-visual */}
        {(() => {
          const multi = slide.visuals && slide.visuals.length > 1;
          const goPrev = () => visualIdx > 0 && setVisualIdx(visualIdx - 1);
          const goNext = () => visualIdx < (slide.visuals?.length ?? 1) - 1 && setVisualIdx(visualIdx + 1);

          const handleDragStart = (clientX) => { if (multi) setDragStartX(clientX); };
          const handleDragEnd = (clientX) => {
            if (dragStartX === null) return;
            const delta = clientX - dragStartX;
            const threshold = 40;
            if (delta < -threshold) goNext();
            else if (delta > threshold) goPrev();
            setDragStartX(null);
          };

          return (
            <div style={isMobile ? { padding: "16px 16px 0", flexShrink: 0, position: "relative" } : { padding: "32px 32px 0", height: 304, flexShrink: 0, position: "relative" }}
              onMouseDown={(e) => handleDragStart(e.clientX)}
              onMouseUp={(e) => handleDragEnd(e.clientX)}
              onMouseLeave={() => setDragStartX(null)}
              onTouchStart={(e) => handleDragStart(e.touches[0].clientX)}
              onTouchEnd={(e) => handleDragEnd(e.changedTouches[0].clientX)}>

              <div style={{ cursor: multi ? "grab" : "default", userSelect: "none" }}>
                {slide.visuals ? slide.visuals[visualIdx] : slide.visual}
              </div>

              {/* Left arrow */}
              {!isMobile && multi && visualIdx > 0 && (
                <button onClick={goPrev}
                  style={{
                    position: "absolute", left: 4, top: "50%", transform: "translateY(-50%)",
                    width: 30, height: 30, borderRadius: "50%",
                    background: "rgba(255,255,255,0.92)", border: `1px solid ${LINE}`,
                    boxShadow: SHADOW_SM,
                    cursor: "pointer", color: INK, fontSize: 18, fontWeight: 700,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    padding: 0, paddingBottom: 2,
                  }}>
                  ‹
                </button>
              )}

              {/* Right arrow */}
              {!isMobile && multi && visualIdx < slide.visuals.length - 1 && (
                <button onClick={goNext}
                  style={{
                    position: "absolute", right: 4, top: "50%", transform: "translateY(-50%)",
                    width: 30, height: 30, borderRadius: "50%",
                    background: "rgba(255,255,255,0.92)", border: `1px solid ${LINE}`,
                    boxShadow: SHADOW_SM,
                    cursor: "pointer", color: INK, fontSize: 18, fontWeight: 700,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    padding: 0, paddingBottom: 2,
                  }}>
                  ›
                </button>
              )}

              {/* Móvil: pantallas de la diapositiva con botones de 40 px, sin depender del gesto */}
              {isMobile && multi && (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, marginTop: 8 }}>
                  <button onClick={goPrev} disabled={visualIdx === 0} aria-label={lang === "es" ? "Pantalla anterior" : "Previous screen"}
                    style={{ width: 40, height: 40, borderRadius: "50%", border: `1.5px solid ${LINE}`, background: WHITE, color: visualIdx === 0 ? LINE : INK, fontSize: 20, fontWeight: 700, cursor: visualIdx === 0 ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0, paddingBottom: 2, fontFamily: "inherit" }}>
                    ‹
                  </button>
                  <div style={{ display: "flex", gap: 5 }}>
                    {slide.visuals.map((_, i) => (
                      <div key={i} style={{ width: i === visualIdx ? 16 : 5, height: 5, borderRadius: 4, background: i === visualIdx ? BRAND : FAINT, transition: "all 0.2s" }} />
                    ))}
                  </div>
                  <button onClick={goNext} disabled={visualIdx === slide.visuals.length - 1} aria-label={lang === "es" ? "Pantalla siguiente" : "Next screen"}
                    style={{ width: 40, height: 40, borderRadius: "50%", border: `1.5px solid ${LINE}`, background: WHITE, color: visualIdx === slide.visuals.length - 1 ? LINE : INK, fontSize: 20, fontWeight: 700, cursor: visualIdx === slide.visuals.length - 1 ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0, paddingBottom: 2, fontFamily: "inherit" }}>
                    ›
                  </button>
                </div>
              )}

              {/* Sub-dots */}
              {!isMobile && multi && (
                <div style={{
                  position: "absolute", left: 0, right: 0, bottom: -8,
                  display: "flex", justifyContent: "center", gap: 5,
                }}>
                  {slide.visuals.map((_, i) => (
                    <div key={i}
                      onClick={() => setVisualIdx(i)}
                      style={{
                        width: i === visualIdx ? 16 : 5, height: 5, borderRadius: 4,
                        background: i === visualIdx ? BRAND : FAINT,
                        cursor: "pointer", transition: "all 0.2s",
                      }} />
                  ))}
                </div>
              )}
            </div>
          );
        })()}

        {/* Content area — fixed min-height */}
        <div style={{
          padding: isMobile ? "20px 16px 16px" : "28px 48px 0",
          textAlign: isMobile ? "left" : "center",
          minHeight: isMobile ? 0 : 140,
          flexShrink: 0,
        }}>
          <h2 style={{
            fontSize: isMobile ? 20 : 22, fontWeight: 800, color: INK,
            letterSpacing: -0.4, marginBottom: isMobile ? 8 : 12, marginTop: 0,
          }}>{slide.title}</h2>
          <p style={{
            fontSize: isMobile ? 15 : 14, color: TEXT, lineHeight: 1.6, margin: 0,
            maxWidth: 580, marginLeft: "auto", marginRight: "auto",
          }}>{slide.body}</p>
        </div>
        </div>

        {!isMobile && <div style={{ flex: 1 }} />}

        {/* Dots */}
        <div style={{
          display: "flex", justifyContent: "center", gap: 8,
          padding: isMobile ? "10px 0 4px" : "12px 0",
          flexShrink: 0,
          ...(isMobile ? { gap: 6, borderTop: `1px solid ${ROW_LINE}` } : null),
        }}>
          {slides.map((_, i) => (
            <div key={i}
              onClick={() => setStep(i)}
              style={{
                width: i === step ? 22 : 7, height: 7, borderRadius: 4,
                background: i === step ? BRAND : LINE,
                cursor: "pointer", transition: "all 0.2s",
              }} />
          ))}
        </div>

        {/* Nav buttons */}
        {isMobile ? (
          <div style={{ padding: "8px 16px calc(12px + env(safe-area-inset-bottom))", display: "flex", gap: 8, flexShrink: 0 }}>
            {!isFirst && (
              <button onClick={handleBack}
                style={{ flex: 1, height: 48, borderRadius: 12, border: `1.5px solid ${LINE}`, background: WHITE, color: TEXT, fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                {lang === "es" ? "Atrás" : "Back"}
              </button>
            )}
            <button onClick={handleNext}
              style={{ flex: 2, height: 48, borderRadius: 12, border: "none", background: BRAND, color: WHITE, fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", padding: "0 12px" }}>
              {isLast ? (slide.cta || (lang === "es" ? "Empezar" : "Get started")) : (lang === "es" ? "Siguiente" : "Next")}
            </button>
          </div>
        ) : (
        <div style={{
          padding: "12px 32px 28px", display: "flex",
          justifyContent: "space-between", alignItems: "center",
          flexShrink: 0,
        }}>
          <button onClick={handleBack}
            disabled={isFirst}
            style={{
              background: "transparent", border: "none",
              color: isFirst ? LINE : TEXT, fontSize: 13, fontWeight: 600,
              cursor: isFirst ? "default" : "pointer", padding: "10px 14px",
              borderRadius: 8, transition: "all 0.15s",
              visibility: isFirst ? "hidden" : "visible",
            }}>
            {lang === "es" ? "← Atrás" : "← Back"}
          </button>

          <button onClick={handleSkip}
            style={{
              background: "transparent", border: "none",
              color: MUTED, fontSize: 13, fontWeight: 600,
              cursor: "pointer", padding: "10px 14px",
              borderRadius: 8, transition: "color 0.15s",
            }}
            onMouseEnter={e => { e.currentTarget.style.color = INK; }}
            onMouseLeave={e => { e.currentTarget.style.color = MUTED; }}
          >
            {help ? (lang === "es" ? "Cerrar" : "Close") : (lang === "es" ? "Saltar tour" : "Skip tour")}
          </button>

          <button onClick={handleNext}
            style={{
              background: BRAND, color: WHITE, border: "none",
              borderRadius: 10, padding: "11px 22px",
              fontWeight: 700, fontSize: 13, cursor: "pointer",
              transition: "all 0.15s",
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-1px)"; }}
            onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; }}
          >
            {isLast ? (slide.cta || (lang === "es" ? "Empezar" : "Get started")) + " →" : (lang === "es" ? "Siguiente →" : "Next →")}
          </button>
        </div>
        )}
      </div>
    </div>
  );
}
